'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'

interface Props {
  isDark: boolean
}

// Tech has a Spanish URL alias (/tecnologia) so the address bar reads
// naturally in either language. Both URLs render the same component.
const LINKS = [
  { key: 'home',  hrefEs: '/',           hrefEn: '/'      },
  { key: 'tech',  hrefEs: '/tecnologia', hrefEn: '/tech'  },
  { key: 'about', hrefEs: '/about',      hrefEn: '/about' },
] as const

const EARTH_80  = 'rgba(81, 50, 5, 0.80)'
const EARTH_100 = 'rgba(81, 50, 5, 1.00)'
const CREAM_80  = 'rgba(245, 238, 217, 0.80)'
const CREAM_100 = 'rgba(245, 238, 217, 1.00)'
const OLIVE     = '#5B7005'
const LIME      = '#9FAF08'

export default function NavLinks({ isDark }: Props) {
  const pathname = usePathname()
  const t = useTranslations('nav')
  const rawLocale = useLocale()
  const isEs = rawLocale !== 'en'

  const baseColor   = isDark ? CREAM_80  : EARTH_80
  const hoverColor  = isDark ? CREAM_100 : EARTH_100
  const activeColor = isDark ? LIME      : OLIVE

  return (
    <nav aria-label="Primary" style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
      {LINKS.map(({ key, hrefEs, hrefEn }) => {
        const href = isEs ? hrefEs : hrefEn
        // Active matches either locale's URL for the same logical page.
        const isActive = pathname === hrefEs || pathname === hrefEn
        return (
          <Link
            key={key}
            href={href}
            className="nav-link"
            data-active={isActive || undefined}
            aria-current={isActive ? 'page' : undefined}
            style={{
              position: 'relative',
              padding: '6px 0',
              textDecoration: 'none',
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
              fontSize: 15,
              letterSpacing: '0.01em',
              color: isActive ? activeColor : baseColor,
              transition: 'color 300ms ease',
              ['--nav-link-hover'  as string]: hoverColor,
              ['--nav-link-active' as string]: activeColor,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: -14,
                top: '50%',
                width: 4, height: 4,
                borderRadius: '50%',
                background: LIME,
                boxShadow: `0 0 6px ${LIME}`,
                transform: `translateY(-50%) scale(${isActive ? 1 : 0})`,
                opacity: isActive ? 1 : 0,
                transition: 'opacity 200ms ease, transform 200ms ease',
                pointerEvents: 'none',
              }}
            />
            {t(key)}
          </Link>
        )
      })}
    </nav>
  )
}
